import React, { useState, useMemo, useEffect } from "react";
import { Input } from "../../components/ui/input";
import { Slider } from "../../components/ui/slider";
import { Button } from "../../components/ui/button";
import { Save } from "lucide-react";
import { tr } from "../../i18n/LangContext";

const fmtMad = (n) => `${Math.round(n).toLocaleString()} MAD`;

const SCENARIOS = { Conservative: 0.8, Expected: 1, Optimistic: 1.2 };

const FIELDS = [
  ["monthlyRent", "Monthly Rent"], ["deposit", "Deposit"], ["agencyFee", "Agency Fee"],
  ["furniture", "Furniture Cost"], ["utilities", "Utilities (mo)"], ["internet", "Internet (mo)"],
  ["cleaning", "Cleaning (mo)"], ["maintenance", "Maintenance Reserve"], ["misc", "Misc Costs"],
];

export default function FinancialAnalysis({ property, currency, onSave }) {
  const sym = currency?.symbol || "£";
  const toCur = (mad) => Math.round(currency ? currency.convert(mad) : (mad || 0) / 12.5);
  const fmtCur = (mad) => `${sym}${toCur(mad).toLocaleString()}`;
  const init = property.financials || {};
  const [scenario, setScenario] = useState("Expected");
  const [vals, setVals] = useState({
    monthlyRent: init.monthlyRent ?? property.rentMad ?? property.priceMad ?? 0,
    deposit: init.deposit ?? 0, agencyFee: init.agencyFee ?? 0, furniture: init.furniture ?? 0,
    utilities: init.utilities ?? 200, internet: init.internet ?? 300, cleaning: init.cleaning ?? 800,
    maintenance: init.maintenance ?? 500, misc: init.misc ?? 0,
    nightlyRate: init.nightlyRate ?? property.airbnb?.nightly_mad ?? 0,
    occupancy: init.occupancy ?? 60,
  });

  const set = (k) => (e) => setVals((v) => ({ ...v, [k]: Number(e.target.value) || 0 }));

  const r = useMemo(() => {
    const occ = Math.min(100, (vals.occupancy * SCENARIOS[scenario])) / 100;
    const nights = 30 * occ;
    const revenue = vals.nightlyRate * nights;
    const expenses = vals.monthlyRent + vals.utilities + vals.internet + vals.cleaning + vals.maintenance + vals.misc;
    const profit = revenue - expenses;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    const upfront = vals.deposit + vals.agencyFee + vals.furniture;
    const breakeven = vals.nightlyRate > 0 ? expenses / vals.nightlyRate : 0;
    const annual = profit * 12;
    const coc = upfront > 0 ? (annual / upfront) * 100 : 0;
    const payback = profit > 0 && upfront > 0 ? upfront / profit : 0;
    return { revenue, expenses, profit, margin, annual, breakeven, coc, payback };
  }, [vals, scenario]);

  const handleSave = () => {
    const score = Math.max(1, Math.min(100, Math.round(50 + r.margin * 0.8 + (r.coc > 0 ? Math.min(r.coc, 40) : -20))));
    onSave({ financials: { ...vals, scenario }, score });
  };

  const max = Math.max(r.revenue, r.expenses, 1);
  const bars = [["Revenue", r.revenue, "#1aa860"], ["Expenses", r.expenses, "#e63946"], ["Profit", Math.abs(r.profit), r.profit >= 0 ? "#1aa860" : "#e63946"]];
  const negative = r.profit < 0;

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-white">{tr("Financial Analysis")}</h3>
        <div className="flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
          {Object.keys(SCENARIOS).map((s) => (
            <button key={s} onClick={() => setScenario(s)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${scenario === s ? "bg-brand-red text-white" : "text-muted-foreground hover:text-white"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-3 text-[11px] uppercase tracking-wide text-muted-foreground">{tr("Inputs (MAD)")}</p>
          <div className="space-y-2.5">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <label className="text-sm text-muted-foreground">{tr(label)}</label>
                <Input type="number" value={vals[k] || ""} onChange={set(k)}
                  className="h-10 w-40 bg-input/60 border-white/10 focus-visible:ring-brand-red text-right" />
              </div>
            ))}
            <p className="pt-2 text-[11px] uppercase tracking-wide text-muted-foreground">{tr("Revenue")}</p>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-muted-foreground">{tr("Nightly Rate (MAD)")}</label>
              <Input type="number" value={vals.nightlyRate || ""} onChange={set("nightlyRate")}
                className="h-10 w-40 bg-input/60 border-white/10 focus-visible:ring-brand-red text-right" />
            </div>
            <div className="pt-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{tr("Occupancy")}</span>
                <span className="font-medium text-white">{vals.occupancy}%</span>
              </div>
              <Slider value={[vals.occupancy]} min={0} max={100} step={5}
                onValueChange={(v) => setVals((x) => ({ ...x, occupancy: v[0] }))} className="mt-2" />
            </div>
          </div>
        </div>

        <div>
          <p className="mb-3 text-[11px] uppercase tracking-wide text-muted-foreground">{tr("Results")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className={`rounded-xl border p-3 ${negative ? "border-brand-red/30 bg-brand-red/5" : "border-brand-green/30 bg-brand-green/5"}`}>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Monthly Profit")}</p>
              <p className={`font-display text-xl font-semibold ${negative ? "text-brand-red" : "text-brand-green"}`}>{r.profit >= 0 ? "" : "-"}{fmtMad(Math.abs(r.profit))}</p>
              <p className="text-[11px] text-muted-foreground">{fmtCur(r.profit)}</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Margin")}</p>
              <p className="font-display text-xl font-semibold text-white">{r.margin.toFixed(1)}%</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Revenue")}</p>
              <p className="font-display text-lg font-semibold text-white">{fmtMad(r.revenue)}</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Expenses")}</p>
              <p className="font-display text-lg font-semibold text-white">{fmtMad(r.expenses)}</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Annual Profit")}</p>
              <p className="font-display text-lg font-semibold text-white">{r.annual >= 0 ? "" : "-"}{fmtMad(Math.abs(r.annual))}</p>
              <p className="text-[11px] text-muted-foreground">{fmtCur(r.annual)}</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Break-even Nights")}</p>
              <p className="font-display text-lg font-semibold text-white">{Math.ceil(r.breakeven)}/mo</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Cash-on-cash")}</p>
              <p className="font-display text-lg font-semibold text-white">{r.coc.toFixed(1)}%</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tr("Payback")}</p>
              <p className="font-display text-lg font-semibold text-white">{Math.round(r.payback)} mo</p>
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-white/8 bg-white/[0.02] p-4">
            <div className="flex h-32 items-end gap-6">
              {bars.map(([label, val, color]) => (
                <div key={label} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                  <span className="text-[10px] font-medium text-white">{fmtMad(val).replace(" MAD", "")}</span>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(2, Math.round((val / max) * 82))}%`, background: color, transition: "height 0.4s ease" }} />
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-6">
              {bars.map(([label]) => (
                <span key={label} className="flex-1 text-center text-[10px] text-muted-foreground">{tr(label)}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Button onClick={handleSave} className="mt-5 h-12 w-full bg-brand-red hover:bg-brand-redDark text-white font-medium">
        <Save className="h-4 w-4" /> Save & Update Deal Score
      </Button>
    </div>
  );
}
